import type { ApprovalStatus } from "../../../services/orderApprovalApi";

const LABELS: Record<ApprovalStatus, string> = {
  PENDING: "Waiting for customer",
  APPROVED: "Approved",
  CHANGES_REQUESTED: "Change asked",
  EXPIRED: "Link expired",
  REVOKED: "Link stopped",
  SUPERSEDED: "Replaced",
};

export const approvalLabel = (status?: string | null): string =>
  status && status in LABELS ? LABELS[status as ApprovalStatus] : "";

/** Chip colour: green done, orange needs the studio, grey ended, blue waiting. */
export const approvalTone = (status?: string | null): "green" | "orange" | "grey" | "blue" => {
  if (status === "APPROVED") return "green";
  if (status === "CHANGES_REQUESTED" || status === "EXPIRED") return "orange";
  if (status === "PENDING") return "blue";
  return "grey";
};

/** The order list's chip: an approval followed by an order change still waiting on the studio shows that. */
export const approvalChip = (summary?: { status?: string | null; needsReapprovalDecision?: boolean } | null) =>
  summary?.needsReapprovalDecision
    ? { label: "Approved, order changed", tone: "orange" as const }
    : { label: approvalLabel(summary?.status), tone: approvalTone(summary?.status) };

const FIELD_LABELS: Record<string, string> = {
  orderCode: "order number",
  customerName: "customer name",
  customerTitle: "Ma'am/Sir",
  city: "city",
  productName: "product",
  productDesign: "design",
  colour: "colour",
  quantity: "quantity",
  lines: "items",
  specifications: "details",
  images: "photos",
  unitPrice: "price",
  currency: "currency",
  total: "total",
  advance: "advance",
  balance: "balance",
  neededBy: "due date",
};

/** "price, due date" for the order fields a change touched. */
export const changedFieldsText = (fields?: string[] | null): string =>
  (fields || []).map((f) => FIELD_LABELS[f] || f).join(", ");

/** Approval links are for the studio's own staff; the backend answers 403 to anyone else (a super admin included). */
export const isStudioStaff = (role?: string | null): boolean =>
  role === "ORGANIZATION_ADMIN" || role === "ORGANIZATION_MANAGER";
